<script setup>
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import http from '@/api/http'
import Button from 'primevue/button'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Tag from 'primevue/tag'
import Select from 'primevue/select'
import ToggleSwitch from 'primevue/toggleswitch'

const router = useRouter()

const rows = ref([])
const total = ref(0)
const loading = ref(false)
const error = ref('')

const filters = ref({
  type: null,
  onlyActual: false,
  page: 1,
  perPage: 20,
})

const typeOptions = [
  { label: 'Все', value: null },
  { label: 'Росфинмониторинг', value: 'rfm' },
  { label: 'СБ ООН', value: 'un_sc' },
  { label: 'МВК', value: 'mvk' },
]

async function load() {
  loading.value = true
  error.value = ''
  try {
    const params = {
      page: filters.value.page,
      per_page: filters.value.perPage,
    }
    if (filters.value.type) params.type = filters.value.type
    if (filters.value.onlyActual) params.only_actual = 1

    const { data } = await http.get('/scrolls', { params })
    rows.value = data?.items ?? []
    total.value = data?.pagination?.total ?? rows.value.length
  } catch (e) {
    error.value = e?.response?.data?.error?.message || 'Не удалось загрузить список'
  } finally {
    loading.value = false
  }
}

function onPage(event) {
  filters.value.page = event.page + 1
  filters.value.perPage = event.rows
  load()
}

function fmtDate(s) {
  if (!s) return '—'
  try {
    return new Date(s).toLocaleString('ru-RU')
  } catch (_) {
    return s
  }
}

function fmtCalendarDate(s) {
  if (!s) return '—'
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s))
  if (m) return `${m[3]}.${m[2]}.${m[1]}`
  return s
}

function fmtUtcAtom(iso) {
  if (!iso) return '—'
  try {
    return `${new Date(iso).toLocaleString('ru-RU', { timeZone: 'UTC' })} UTC`
  } catch (_) {
    return iso
  }
}

onMounted(load)
</script>

<template>
  <div class="page">
    <div class="toolbar">
      <Select
        v-model="filters.type"
        :options="typeOptions"
        option-label="label"
        option-value="value"
        placeholder="Тип перечня"
        @change="() => { filters.page = 1; load() }"
      />
      <div class="toolbar__switch">
        <ToggleSwitch
          v-model="filters.onlyActual"
          input-id="onlyActual"
          @change="() => { filters.page = 1; load() }"
        />
        <label for="onlyActual">Только актуальные</label>
      </div>

      <div class="toolbar__spacer" />

      <Button
        icon="pi pi-refresh"
        label="Обновить"
        severity="secondary"
        outlined
        @click="load"
        :loading="loading"
      />
      <Button
        icon="pi pi-upload"
        label="Загрузить перечень"
        @click="router.push({ name: 'scrolls.upload' })"
      />
    </div>

    <p v-if="error" class="error">{{ error }}</p>

    <DataTable
      :value="rows"
      :loading="loading"
      lazy
      paginator
      :rows="filters.perPage"
      :total-records="total"
      :rows-per-page-options="[10, 20, 50, 100]"
      @page="onPage"
      data-key="id"
      striped-rows
      class="mt"
    >
      <Column field="type" header="Тип" style="width: 7rem">
        <template #body="{ data }">
          <Tag :value="data.type" severity="info" />
        </template>
      </Column>
      <Column header="Дата перечня" style="width: 9rem">
        <template #body="{ data }">{{ fmtCalendarDate(data.date_load) }}</template>
      </Column>
      <Column header="Публикация (UTC)" style="width: 11rem">
        <template #body="{ data }">{{ fmtUtcAtom(data.list_published_at) }}</template>
      </Column>
      <Column header="Пред. перечень" style="width: 9rem">
        <template #body="{ data }">{{ fmtCalendarDate(data.previous_list_date) }}</template>
      </Column>
      <Column field="source_file" header="Файл" />
      <Column field="subjects_count" header="Записей" style="width: 8rem" />
      <Column header="Актуален" style="width: 8rem">
        <template #body="{ data }">
          <Tag
            v-if="data.is_actual"
            value="актуален"
            severity="success"
          />
          <Tag v-else value="история" severity="secondary" />
        </template>
      </Column>
      <Column header="Загружен" style="width: 12rem">
        <template #body="{ data }">{{ fmtDate(data.date_create) }}</template>
      </Column>
      <template #empty>
        <p class="muted">Перечни не загружены</p>
      </template>
    </DataTable>
  </div>
</template>

<style scoped>
.toolbar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.toolbar__switch {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.toolbar__spacer {
  flex: 1;
}

.error {
  color: #b91c1c;
  margin: 0 0 0.75rem;
}

.mt {
  margin-top: 0.5rem;
}
</style>
